import * as Sentry from '@sentry/react';

/**
 * Centralized logging service for bulk deal documents upload
 */
export const BulkUploadLogger = {
  /**
   * Log an error with context
   */
  error(message: string, error?: Error | unknown, context?: Record<string, unknown>) {
    console.error(`[BulkUpload] ${message}`, error, context);

    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: { feature: 'bulk-deal-docs-upload' },
        contexts: { bulkUpload: context || {} },
        level: 'error',
      });
    } else {
      Sentry.captureMessage(`[BulkUpload] ${message}`, {
        tags: { feature: 'bulk-deal-docs-upload' },
        contexts: { bulkUpload: { ...context, error } },
        level: 'error',
      });
    }
  },

  /**
   * Log a warning with context
   */
  warn(message: string, context?: Record<string, unknown>) {
    console.warn(`[BulkUpload] ${message}`, context);

    Sentry.captureMessage(`[BulkUpload] ${message}`, {
      tags: { feature: 'bulk-deal-docs-upload' },
      contexts: { bulkUpload: context || {} },
      level: 'warning',
    });
  },

  /**
   * Log info message (only to console, not to Sentry)
   */
  info(message: string, context?: Record<string, unknown>) {
    console.info(`[BulkUpload] ${message}`, context);
  },

  /**
   * Log debug message (only to console in dev mode)
   */
  debug(message: string, context?: Record<string, unknown>) {
    if (import.meta.env.DEV) {
      console.debug(`[BulkUpload] ${message}`, context);
    }
  },

  /**
   * Log upload attempt failure (used in retry logic)
   */
  retryAttempt(attempt: number, error: Error, context?: Record<string, unknown>) {
    console.warn(`[BulkUpload] Upload attempt ${attempt} failed, retrying...`, error, context);
    // Don't send every retry to Sentry, only final failures
  },

  /**
   * Log polling status update
   */
  pollStatus(uploadId: number, status: string) {
    if (import.meta.env.DEV) {
      console.debug(`[BulkUpload] Poll status for upload ${uploadId}: ${status}`);
    }
  },
};
